
import {
  defineComponent, PropType, computed,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import Dialog from 'primevue/dialog';
import Button from 'primevue/button';
// eslint-disable-next-line import/no-cycle
import { MARKETPLACE_ENABLED } from '@/router';
import type {
  RequireIntegrationInfo,
  ShopIntegrationStatus,
} from '@/types/integrations';

// The dialog only deals with non-active states — `active` is filtered out
// upstream by `gatePreflight`. The narrower alias keeps the prop typed
// precisely.
export type RequireIntegrationStatus = Exclude<ShopIntegrationStatus, 'active'>;

// Re-exported for backwards compatibility with existing imports.
export type { RequireIntegrationInfo };

export default defineComponent({
  name: 'RequireIntegrationDialog',

  components: {
    Dialog,
    Button,
  },

  props: {
    visible: {
      type: Boolean,
      default: false,
    },

    integration: {
      type: Object as PropType<RequireIntegrationInfo | null>,
      default: null,
    },

    statusLabel: {
      type: String as PropType<RequireIntegrationStatus>,
      default: 'not_installed',
    },
  },

  emits: ['update:visible'],

  setup(props, { emit }) {
    const { t } = useI18n();
    const router = useRouter();

    const visibleLocal = computed({
      get: () => props.visible,
      set: (val: boolean) => emit('update:visible', val),
    });

    const onCancel = () => {
      visibleLocal.value = false;
    };

    const onInstall = () => {
      // Garde-fou : quand la marketplace est masquée le CTA est déjà désactivé,
      // mais on double-vérifie côté logique pour ne jamais naviguer par erreur.
      if (!MARKETPLACE_ENABLED) return;
      // Close the dialog before navigating so it doesn't briefly flash on
      // the marketplace page if the route resolves synchronously.
      visibleLocal.value = false;
      // The marketplace page lists the integration; users land there and
      // click "Install" from the catalogue card. We pre-filter by slug to
      // help them find it instantly.
      router.push({
        name: 'shop.Integrations',
        query: {
          focus: props.integration?.slug ?? undefined,
          focusName: props.integration?.name ?? undefined,
        },
      });
    };

    return {
      t,
      visibleLocal,
      onCancel,
      onInstall,
      marketplaceEnabled: MARKETPLACE_ENABLED,
    };
  },
});
